import { MenuItem } from "@mui/material"
import { ReactElement, useState } from "react"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { LockedDialog, useLockedDialogState } from "../../../../../shared/components/dialogs/LockedDialog"
import { StandardSuccessDialog } from "../../../../../shared/components/dialogs/StandardSuccessDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../shared/components/dialogs/StandardErrorDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { ApiError } from "../../../../../core/api/errorTypes"
import { refundMerchantOrder } from "../../../../receipt/ordersApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { Order } from "../../../controlKitchenTypes"

type MenuActionKey = "showReceipt" | "refund" | "messageCustomer"

type MenuItemConfig = {
    key: MenuActionKey
    element: ReactElement
}

type OrderAccountingMenuProps = Readonly<{
    order: Order
    onShowReceipt: (order: Order) => void
    onMessageCustomer?: (order: Order) => void
    onClose: () => void
    onRefundSuccess?: () => void
}>

export function OrderAccountingMenu({
    order,
    onShowReceipt,
    onMessageCustomer,
    onClose,
    onRefundSuccess,
}: OrderAccountingMenuProps) {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const { errorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const { lockedDialog, openLockedDialog, closeLockedDialog } = useLockedDialogState()
    const { mutateAsync: callRefundOrder, isLoading: isRefunding } = useApiMutation(refundMerchantOrder, {
        async onSuccess() {
            setShowConfirmModal(false)
            setShowSuccessModal(true)
        },
        async onError(_, error) {
            setShowConfirmModal(false)
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined
            openErrorDialog({
                title: l10n.getString("order-accounting-refund-pos-order-title"),
                description: displayMessage || l10n.getString("transactions-table-list-refund-error-message"),
            })
        },
    })

    const handleShowReceipt = () => {
        onShowReceipt(order)
        onClose()
    }

    const handleRefundClick = () => {
        const refundAction = order.orderAction?.refundPOSOrder
        if (refundAction?.allow) {
            setShowConfirmModal(true)
        } else {
            openLockedDialog({
                title:
                    refundAction?.message?.title ||
                    l10n.getString("order-accounting-refund-pos-order-locked-dialog-title"),
                body:
                    refundAction?.message?.body ||
                    l10n.getString("order-accounting-refund-pos-order-locked-dialog-body"),
            })
        }
    }

    const handleMessageCustomer = () => {
        onMessageCustomer?.(order)
        onClose()
    }

    const handleConfirmRefund = () => {
        callRefundOrder({ merchantId, orderId: order.id })
    }

    const menuItemsConfig: MenuItemConfig[] = [
        {
            key: "showReceipt",
            element: (
                <LocalizedStrict id="office-purchase-receipt" key={`receipt-${order.id}`}>
                    <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            key: "refund",
            element: (
                <LocalizedStrict id="order-action-menu-refund" key={`refund-${order.id}`}>
                    <MenuItem onClick={handleRefundClick}>Refund</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            key: "messageCustomer",
            element: (
                <LocalizedStrict id="transactions-table-list-message-customer" key={`message-customer-${order.id}`}>
                    <MenuItem onClick={handleMessageCustomer}>Message customer</MenuItem>
                </LocalizedStrict>
            ),
        },
    ]

    const menuItems = menuItemsConfig
        .filter(({ key }) => {
            if (key === "refund") return order.orderAction?.refundPOSOrder !== undefined
            if (key === "messageCustomer") return onMessageCustomer !== undefined
            return true
        })
        .map(({ element }) => element)

    return (
        <>
            {menuItems}

            <LocalizedStrict
                id="order-accounting-refund-pos-order-confirm-dialog"
                attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                vars={{ orderId: order.id.toString() }}
            >
                <ConfirmModal
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    title="Refund"
                    message="Are you sure you want to refund order?"
                    handleClick={handleConfirmRefund}
                    buttonLabelSubmit="Confirm"
                    buttonLabelCancel="Cancel"
                    buttonSubmitDisabled={isRefunding}
                    autoCloseAfterSubmit={false}
                />
            </LocalizedStrict>

            {showSuccessModal && (
                <StandardSuccessDialog
                    open={showSuccessModal}
                    dialogType="refundOrder"
                    onClose={() => {
                        setShowSuccessModal(false)
                        onRefundSuccess?.()
                    }}
                />
            )}

            {errorDialog.open && (
                <StandardErrorDialog
                    dialogDetails={errorDialog}
                    onClose={() => {
                        closeErrorDialog()
                        onRefundSuccess?.()
                    }}
                />
            )}

            {lockedDialog.open && <LockedDialog dialogDetails={lockedDialog} onClose={closeLockedDialog} />}
        </>
    )
}
